import React, { memo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Badge, Card, CardImage, JobCard, Loading } from 'components'
import styles from '../styles'
import {
	TTopResultsProps,
	EEntity,
	IArticle,
	EAppStateActionTypes,
	ETextFontSize,
} from 'models'
import { StoreValue } from 'store'
import { SearchEmptyResult } from '.'

const useStyles = makeStyles(styles)
const TopResults: React.FunctionComponent<TTopResultsProps> = props => {
	const { history, search, storeDispatch, storeSearchLoading } = props
	const classes = useStyles()
	const { t, i18n } = useTranslation()

	const clearQueryHandle = useCallback(() => {
		storeDispatch({
			type: EAppStateActionTypes.updateQuery,
			query: '',
		})
	}, [storeDispatch])

	const goToCompany = useCallback(
		(company: any) => () => {
			clearQueryHandle()
			history.push(`/${i18n.language}/employers/${company.slug}`, company)
		},
		[history, i18n.language, clearQueryHandle],
	)
	const goToProfile = useCallback(
		(user: any) => () => {
			clearQueryHandle()
			history.push(`/${i18n.language}/people/${user.uid}`)
		},
		[history, i18n.language, clearQueryHandle],
	)

	const companies = search ? search.Companies : undefined
	const people = search ? search.People : undefined
	const articles = search ? search.Articles : undefined
	const teams = search ? search.Teams : undefined
	const jobs = search ? search.Jobs : undefined

	return (
		<>
			{storeSearchLoading ? (
				<Loading size={40} style={{ margin: '50% auto' }} />
			) : (
				<>
					<div
						className={classes.tabsContentHeadline}
						style={
							companies && companies.length > 0
								? { marginBottom: 40 }
								: {}
						}
					>
						{t('Employers')}
					</div>

					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerCompany,
							classes.fixFlexList,
						)}
					>
						{companies &&
							companies.map((item: any, index: any) => (
								<CardImage
									company={item}
									key={`company_${index}`}
									onClickBadge={goToCompany}
								/>
							))}
						{companies && companies.length === 0 && (
							<SearchEmptyResult
								textFontSize={ETextFontSize.small}
							/>
						)}
					</div>

					<hr className={classes.delimiter} />
					<div
						className={classes.tabsContentHeadline}
						style={
							people && people.length > 0
								? { marginBottom: 40 }
								: {}
						}
					>
						{t('People')}
					</div>
					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.fixFlexList,
						)}
					>
						{people &&
							people.map((item: any, index: any) => (
								<Badge
									{...item}
									title={`${item.profile.firstName} ${item.profile.lastName}`}
									user={item}
									type={EEntity.person}
									key={`people_${index}`}
									extraClasses={classes.companyBadge}
									currentPositionClasses={
										classes.peopleBadgeCurrentPosition
									}
									onClick={goToProfile(item)}
								/>
							))}
						{people && people.length === 0 && (
							<SearchEmptyResult
								textFontSize={ETextFontSize.small}
							/>
						)}
					</div>
					<hr className={classes.delimiter} />
					<div
						className={classes.tabsContentHeadline}
						style={
							articles && articles.length > 0
								? { marginBottom: 40 }
								: {}
						}
					>
						{t('Articles')}
					</div>
					<div className={classes.tabsContentContainer}>
						{articles &&
							articles.map((item: IArticle, index: any) => (
								<Card
									key={item.id}
									article={item}
									extraClassesContainer={
										classes.articlesContainer
									}
								/>
							))}
						{articles && articles.length === 0 && (
							<SearchEmptyResult
								textFontSize={ETextFontSize.small}
							/>
						)}
					</div>
					<hr className={classes.delimiter} />
					<div
						className={classes.tabsContentHeadline}
						style={
							teams && teams.length > 0
								? { marginBottom: 40 }
								: {}
						}
					>
						{t('Teams')}
					</div>

					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerTeams,
							classes.fixFlexList,
						)}
					>
						{teams &&
							teams.map((item: any, index: any) => (
								<CardImage
									team={item}
									key={`teams_${index}`}
									onClickBadge={() => {}}
								/>
							))}
						{teams && teams.length === 0 && (
							<SearchEmptyResult
								textFontSize={ETextFontSize.small}
							/>
						)}
					</div>
					<hr className={classes.delimiter} />
					<div
						className={classes.tabsContentHeadline}
						style={
							jobs && jobs.length > 0 ? { marginBottom: 40 } : {}
						}
					>
						{t('Jobs')}
					</div>

					<div
						className={classNames(
							classes.tabsContentContainer,
							classes.tabsContentContainerTeams,
							classes.fixFlexList,
						)}
					>
						{jobs.length === 0 ? (
							<SearchEmptyResult
								textFontSize={ETextFontSize.small}
							/>
						) : (
							jobs.map((item: any, index: any) => (
								<JobCard
									data={item}
									key={`jobs_${index}`}
									onClickBadge={() => {}}
								/>
							))
						)}
					</div>
				</>
			)}
		</>
	)
}

export default memo((props: TTopResultsProps) => (
	<StoreValue
		keys={[
			'storeDispatch',
			'search.section|storeSearchSection',
			'search.loading|storeSearchLoading',
		]}
	>
		<TopResults {...props} />
	</StoreValue>
))
